import React, { useCallback, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { Link } from 'react-router-dom';

import NavigationBar from '../Shared/NavigationBar';
import RulesSidebarNav from './RulesSidebarNav';
import RulesDisclaimer from '../Shared/RulesDisclaimer';
import useScrollToTop from '../../helpers/useScrollToTop';
import { STATE_BASED_ACTIONS, SBA_CATEGORIES } from '../../helpers/rulesData';
import { usePreferences } from '../Preferences/PreferencesContext';
import { getThemeColors } from '../../helpers/themeColors';

const SPACING = {
  containerMaxWidth: 1100,
  containerPadding: 24,
  containerPaddingMobile: 12,
  headerMarginTop: 24,
  headerMarginBottom: 24,
  titleMarginBottom: 8,
  sectionMarginBottom: 32,
  panelMarginBottom: 16,
  exampleMarginBottom: 12,
  examplePadding: 16,
  exampleBorderRadius: 6,
  exampleBorderLeft: 3,
  sidebarWidth: 220,
  sidebarGap: 32,
  sidebarPadding: 16,
  sidebarBorderRadius: 6,
  sidebarTitleMarginBottom: 8,
  sidebarItemPadding: '6px 0',
  stickyTop: 20,
  backLinkMarginBottom: 16,
  backLinkPadding: '10px 0',
  introPadding: 20,
  introBorderRadius: 8,
  cardLinkPadding: '1px 4px',
  cardLinkBorderRadius: 3,
  exampleLabelMarginBottom: 6,
  exampleScenarioMarginBottom: 8,
  paragraphMarginBottom: 12,
  sbaItemMarginBottom: 12,
  sbaItemPadding: 16,
  sbaItemBorderRadius: 6,
  rulesRefPadding: '2px 8px',
  rulesRefBorderRadius: 4,
  faqPadding: 20,
  faqBorderRadius: 8,
  faqItemMarginBottom: 16,
  faqQuestionMarginBottom: 6,
  categoryMarginBottom: 8,
};

const FONT = {
  title: 28,
  titleMobile: 22,
  subtitle: 15,
  subtitleMobile: 14,
  sidebarTitle: 14,
  sidebarTitleWeight: 600,
  sidebarTitleLetterSpacing: 1,
  sidebarItem: 14,
  sectionHeading: 22,
  sectionHeadingWeight: 600,
  categoryHeading: 18,
  categoryHeadingWeight: 600,
  sbaName: 16,
  sbaNameWeight: 600,
  sbaDescription: 14,
  sbaDescriptionLineHeight: 1.7,
  rulesRef: 14,
  rulesRefWeight: 600,
  introText: 14,
  introTextLineHeight: 1.7,
  exampleLabel: 14,
  exampleLabelWeight: 600,
  exampleLabelLetterSpacing: 1,
  exampleScenario: 14,
  exampleScenarioWeight: 600,
  exampleResolution: 14,
  exampleResolutionLineHeight: 1.6,
  backLink: 14,
  cardLink: 14,
  faqQuestion: 15,
  faqQuestionWeight: 600,
  faqAnswer: 14,
  faqAnswerLineHeight: 1.7,
};

const CARD_NAME_REGEX = /\b(Lightning Bolt|Shock|Greed|Brainstorm|Thought Scour|Ichor Rats|Sheoldred, the Apocalypse|Clone|Jace, the Mind Sculptor|Pacifism|Unsummon|Sword of Fire and Ice)\b/g;

const renderTextWithCardLinks = (text, colors) => {
  const parts = [];
  let lastIndex = 0;
  let match;

  const regex = new RegExp(CARD_NAME_REGEX.source, 'g');
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const cardName = match[0];
    parts.push(
      <Link
        key={`${cardName}-${match.index}`}
        to={`/cardsearch/${encodeURIComponent(cardName)}`}
        style={{
          color: colors.accentPrimary,
          backgroundColor: colors.accentBg,
          padding: SPACING.cardLinkPadding,
          borderRadius: SPACING.cardLinkBorderRadius,
          fontSize: FONT.cardLink,
          textDecoration: 'none',
        }}
      >
        {cardName}
      </Link>
    );
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length > 0 ? parts : text;
};

const CATEGORY_ORDER = [
  SBA_CATEGORIES.PLAYER,
  SBA_CATEGORIES.CREATURE,
  SBA_CATEGORIES.PERMANENT,
  SBA_CATEGORIES.COUNTER,
];

const CATEGORY_ICONS = {
  [SBA_CATEGORIES.PLAYER]: '👤',
  [SBA_CATEGORIES.CREATURE]: '🐉',
  [SBA_CATEGORIES.PERMANENT]: '🏛️',
  [SBA_CATEGORIES.COUNTER]: '🔢',
};

const FAQ_ITEMS = [
  {
    id: 'faq-no-stack',
    question: 'Do state-based actions use the stack?',
    answer:
      'No. State-based actions do not use the stack and cannot be responded to. They simply happen whenever the game checks for them (right before any player would receive priority). You cannot cast a spell or activate an ability "in response to" an SBA.',
  },
  {
    id: 'faq-indestructible',
    question: 'Does indestructible prevent all state-based actions?',
    answer:
      'No. Indestructible prevents destruction from lethal damage (rule 704.5g), but it does NOT prevent a creature from being put into the graveyard for having 0 or less toughness (rule 704.5f). A creature with indestructible and 0 toughness will still die. Similarly, indestructible does not prevent the legend rule or a planeswalker with 0 loyalty from being put into the graveyard.',
  },
  {
    id: 'faq-simultaneous',
    question: 'Can multiple SBAs happen at the same time?',
    answer:
      'Yes. All applicable state-based actions are performed simultaneously as a single event. For example, if two creatures both have lethal damage, they are both destroyed at the same time. After all SBAs are performed, the game checks again. This cycle repeats until no more SBAs apply, then players receive priority.',
  },
  {
    id: 'faq-when-checked',
    question: 'When exactly are state-based actions checked?',
    answer:
      'State-based actions are checked whenever a player would receive priority. This includes the beginning of each step and phase, and after each spell or ability resolves. They are also checked during the cleanup step. The game keeps checking and performing SBAs in a loop until none apply.',
  },
  {
    id: 'faq-damage-vs-toughness',
    question: 'What is the difference between lethal damage and 0 toughness?',
    answer:
      'Lethal damage (704.5g) means a creature has damage marked on it equal to or greater than its toughness. This causes destruction, which is prevented by indestructible. Having 0 or less toughness (704.5f) means the creature\'s toughness itself is reduced to 0 or below (typically by -X/-X effects or counters). This puts the creature into the graveyard without destroying it, so indestructible does not help.',
  },
];

const groupedSBAs = CATEGORY_ORDER.reduce((acc, cat) => {
  acc[cat] = STATE_BASED_ACTIONS.filter((sba) => sba.category === cat);
  return acc;
}, {});

const SIDEBAR_ITEMS = [
  { id: 'intro', label: 'Introduction' },
  ...CATEGORY_ORDER.map((cat) => ({
    id: `cat-${cat.toLowerCase()}`,
    label: `${CATEGORY_ICONS[cat]} ${cat} SBAs`,
  })),
  { id: 'faq', label: 'Common Misconceptions' },
];

const SBAReference = () => {
  const { preferences } = usePreferences();
  const t = getThemeColors(preferences.theme);
  const [activeId, setActiveId] = useState('intro');

  useScrollToTop();

  const scrollToSection = useCallback((sectionId) => {
    setActiveId(sectionId);
    const el = document.getElementById(sectionId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const renderExamples = (examples, keyPrefix) =>
    examples.map((ex, idx) => (
      <div
        key={`${keyPrefix}-ex-${idx}`}
        style={{
          backgroundColor: t.cardBackground,
          borderLeft: `${SPACING.exampleBorderLeft}px solid ${t.accentSecondary}`,
          borderRadius: SPACING.exampleBorderRadius,
          padding: SPACING.examplePadding,
          marginBottom: SPACING.exampleMarginBottom,
        }}
      >
        <div
          style={{
            fontSize: FONT.exampleLabel,
            fontWeight: FONT.exampleLabelWeight,
            color: t.accentPrimary,
            textTransform: 'uppercase',
            letterSpacing: FONT.exampleLabelLetterSpacing,
            marginBottom: SPACING.exampleLabelMarginBottom,
          }}
        >
          Example {examples.length > 1 ? idx + 1 : ''}
        </div>
        <div
          style={{
            fontSize: FONT.exampleScenario,
            fontWeight: FONT.exampleScenarioWeight,
            color: t.textPrimary,
            marginBottom: SPACING.exampleScenarioMarginBottom,
          }}
        >
          {renderTextWithCardLinks(ex.scenario, t)}
        </div>
        <div
          style={{
            fontSize: FONT.exampleResolution,
            color: t.textSecondary,
            lineHeight: FONT.exampleResolutionLineHeight,
          }}
        >
          {renderTextWithCardLinks(ex.resolution, t)}
        </div>
      </div>
    ));

  return (
    <>
      <NavigationBar />
      <div
        style={{
          maxWidth: SPACING.containerMaxWidth,
          margin: '0 auto',
          padding: isMobile ? SPACING.containerPaddingMobile : SPACING.containerPadding,
        }}
      >
        {/* Back link */}
        <div style={{ marginBottom: SPACING.backLinkMarginBottom }}>
          <Link to="/rules" style={{ color: t.accentPrimary, fontSize: FONT.backLink, textDecoration: 'none', display: 'inline-block', padding: SPACING.backLinkPadding }}>
            ← Back to Rules Reference
          </Link>
        </div>

        {/* Header */}
        <div style={{ marginTop: SPACING.headerMarginTop, marginBottom: SPACING.headerMarginBottom }}>
          <h1 style={{ fontSize: isMobile ? FONT.titleMobile : FONT.title, color: t.textPrimary, marginBottom: SPACING.titleMarginBottom }}>
            ⚡ State-Based Actions
          </h1>
          <div style={{ fontSize: isMobile ? FONT.subtitleMobile : FONT.subtitle, color: t.textMuted }}>
            Game actions that happen automatically without using the stack
          </div>
        </div>

        {/* Layout: sidebar + content */}
        <div style={{ display: 'flex', gap: isMobile ? 0 : SPACING.sidebarGap, alignItems: 'flex-start', flexDirection: isMobile ? 'column' : 'row' }}>
          {/* Sidebar Navigation - responsive layout handled by component */}
          <RulesSidebarNav
            sections={SIDEBAR_ITEMS}
            activeId={activeId}
            onSelect={scrollToSection}
          />

          {/* Main content */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Introduction */}
            <div id="intro" style={{ marginBottom: SPACING.sectionMarginBottom }}>
              <div
                style={{
                  backgroundColor: t.backgroundSecondary,
                  border: `1px solid ${t.borderPrimary}`,
                  borderRadius: SPACING.introBorderRadius,
                  padding: SPACING.introPadding,
                }}
              >
                <div style={{ fontSize: FONT.introText, color: t.textSecondary, lineHeight: FONT.introTextLineHeight }}>
                  <p style={{ marginBottom: SPACING.paragraphMarginBottom }}>
                    <strong style={{ color: t.textPrimary }}>State-based actions (SBAs)</strong> are game actions that happen
                    automatically whenever the game checks for them. They do not use the stack and cannot be responded to.
                  </p>
                  <p style={{ marginBottom: SPACING.paragraphMarginBottom }}>
                    The game checks for state-based actions whenever a player would receive priority. If any SBAs apply,
                    they are all performed <strong style={{ color: t.textPrimary }}>simultaneously</strong>, then the game
                    checks again. This cycle repeats until no more SBAs apply, at which point players receive priority.
                  </p>
                  <p style={{ margin: 0 }}>
                    Because SBAs don't use the stack, you cannot "respond" to a creature dying from lethal damage or a
                    player losing from 0 life. By the time you get priority, the SBA has already happened.
                  </p>
                </div>
              </div>
            </div>

            {/* Category Panels */}
            {CATEGORY_ORDER.map((category) => (
              <div
                key={category}
                id={`cat-${category.toLowerCase()}`}
                style={{ marginBottom: SPACING.sectionMarginBottom }}
              >
                <h2 style={{
                  fontSize: FONT.sectionHeading,
                  fontWeight: FONT.sectionHeadingWeight,
                  color: t.textPrimary,
                  marginBottom: SPACING.categoryMarginBottom,
                }}>
                  {CATEGORY_ICONS[category]} {category} State-Based Actions
                </h2>

                {groupedSBAs[category].map((sba) => (
                  <div
                    key={sba.id}
                    style={{
                      backgroundColor: t.backgroundSecondary,
                      border: `1px solid ${t.borderPrimary}`,
                      borderRadius: SPACING.sbaItemBorderRadius,
                      padding: SPACING.sbaItemPadding,
                      marginBottom: SPACING.sbaItemMarginBottom,
                    }}
                  >
                    {/* SBA header */}
                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', marginBottom: SPACING.exampleLabelMarginBottom }}>
                      <span style={{ fontSize: FONT.sbaName, fontWeight: FONT.sbaNameWeight, color: t.textPrimary, marginRight: SPACING.sbaItemMarginBottom }}>
                        {sba.name}
                      </span>
                      <span
                        style={{
                          fontSize: FONT.rulesRef,
                          fontWeight: FONT.rulesRefWeight,
                          color: t.accentPrimary,
                          backgroundColor: t.accentBg,
                          padding: SPACING.rulesRefPadding,
                          borderRadius: SPACING.rulesRefBorderRadius,
                        }}
                      >
                        {sba.rulesReference}
                      </span>
                    </div>

                    {/* Description */}
                    <div
                      style={{
                        fontSize: FONT.sbaDescription,
                        color: t.textSecondary,
                        lineHeight: FONT.sbaDescriptionLineHeight,
                        marginBottom: sba.examples && sba.examples.length > 0 ? SPACING.panelMarginBottom : 0,
                      }}
                    >
                      {renderTextWithCardLinks(sba.description, t)}
                    </div>

                    {/* Examples */}
                    {sba.examples && sba.examples.length > 0 && (
                      <div>
                        {renderExamples(sba.examples, sba.id)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}

            {/* FAQ / Common Misconceptions */}
            <div id="faq" style={{ marginBottom: SPACING.sectionMarginBottom }}>
              <h2 style={{ fontSize: FONT.sectionHeading, fontWeight: FONT.sectionHeadingWeight, color: t.textPrimary, marginBottom: SPACING.panelMarginBottom }}>
                Common Misconceptions
              </h2>
              <div
                style={{
                  backgroundColor: t.cardBackground,
                  border: `1px solid ${t.borderPrimary}`,
                  borderRadius: SPACING.faqBorderRadius,
                  padding: SPACING.faqPadding,
                }}
              >
                {FAQ_ITEMS.map((faq, idx) => (
                  <div
                    key={faq.id}
                    style={{
                      marginBottom: idx < FAQ_ITEMS.length - 1 ? SPACING.faqItemMarginBottom : 0,
                      paddingBottom: idx < FAQ_ITEMS.length - 1 ? SPACING.faqItemMarginBottom : 0,
                      borderBottom: idx < FAQ_ITEMS.length - 1 ? `1px solid ${t.borderPrimary}` : 'none',
                    }}
                  >
                    <div style={{ fontSize: FONT.faqQuestion, fontWeight: FONT.faqQuestionWeight, color: t.textPrimary, marginBottom: SPACING.faqQuestionMarginBottom }}>
                      Q: {faq.question}
                    </div>
                    <div style={{ fontSize: FONT.faqAnswer, color: t.textSecondary, lineHeight: FONT.faqAnswerLineHeight }}>
                      {faq.answer}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <RulesDisclaimer />
      </div>
    </>
  );
};

export default SBAReference;
